import classNames from "classnames";
import config from "config";
import qs from "qs";
import { useTranslation } from "react-i18next";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

import { useConfigContext } from "shared/contexts/ConfigContext";

import { DOCS_RELEASE_NOTES_TAB_KEY } from "pages/Docs/constants";
import { hasSeenLatestReleaseNotes } from "pages/Docs/utils";

import GarageHomeIcon from "features/ui/Icons/GarageHomeIcon";

import { routes } from "services/routes";
import { RouteConfig } from "services/routesConfig";

import {
  SIDEBAR_COLLAPSED_WIDTH_CLASS,
  SIDEBAR_EXPANDED_WIDTH_CLASS,
} from "./constants";
import { useSchemaDashboardNavItem } from "./hooks";
import NavBarItem, { ExternalNavBarItem } from "./NavBarItem";
import SideMenuGroup from "./SideMenuGroup";
import { NavGroup, NavItem } from "./types";
import ViaductLogoSection from "./ViaductLogoSection";

interface Props {
  navItems?: NavItem[];
  navGroups: NavGroup[];
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  toggleExpand: () => void;
  routeConfig: RouteConfig;
  showHomeLink?: boolean;
}

const Sidebar = ({
  navItems,
  navGroups,
  expanded,
  setExpanded,
  toggleExpand,
  showHomeLink,
}: Props) => {
  const { t } = useTranslation("pages");
  const {
    pages: { landingPage, releaseNotes },
  } = useConfigContext();
  const showReleaseNotesIcon = !hasSeenLatestReleaseNotes();
  const showDashboard =
    showHomeLink && landingPage && landingPage.views?.length > 0;
  const schemaDashboardItem = useSchemaDashboardNavItem();
  const { loaded } = useConfigContext();

  if (!config.isInstanceManagement() && !loaded) {
    return null;
  }

  return (
    <nav
      className={classNames("ViaductSidebar-root ", {
        [SIDEBAR_EXPANDED_WIDTH_CLASS]: expanded,
        [SIDEBAR_COLLAPSED_WIDTH_CLASS]: !expanded,
      })}
    >
      <div className="flex flex-1 overflow-x-hidden flex-col">
        <ul>
          <li>
            <ViaductLogoSection
              expanded={expanded}
              toggleExpand={toggleExpand}
            />
          </li>
        </ul>
        {(showDashboard || schemaDashboardItem) && (
          <ul className="mt-2.5">
            {showDashboard && (
              <NavBarItem
                item={{
                  to: routes.root,
                  text: t("dashboard.title"),
                  icon: <GarageHomeIcon />,
                }}
                menuExpanded={expanded}
              />
            )}
            {schemaDashboardItem && (
              <NavBarItem item={schemaDashboardItem} menuExpanded={expanded} />
            )}
          </ul>
        )}
        <div
          className={classNames("scrollbar-slim overflow-y-auto mb-2.5", {
            "mt-2.5": !showDashboard && !schemaDashboardItem,
          })}
        >
          <ul id="main-menu">
            {navItems &&
              navItems.map((item) =>
                item.isExternal ? (
                  <ExternalNavBarItem
                    key={item.testId ?? item.to.toString()}
                    link={item}
                    menuExpanded={expanded}
                  />
                ) : (
                  <NavBarItem
                    key={item.to.toString()}
                    item={item}
                    menuExpanded={expanded}
                  />
                )
              )}
            {navGroups.map((group) => (
              <SideMenuGroup
                key={group.title}
                menuExpanded={expanded}
                {...group}
              />
            ))}
          </ul>
        </div>
        <ul className="border-t border-gray-200 mb-3">
          <NavBarItem
            item={{
              to: {
                pathname: routes.docs,
                search:
                  showReleaseNotesIcon && releaseNotes
                    ? qs.stringify({ tab: DOCS_RELEASE_NOTES_TAB_KEY })
                    : "",
              },
              text: t("help.title"),
              icon:
                showReleaseNotesIcon && releaseNotes
                  ? CardGiftcardOutlinedIcon
                  : HelpOutlineOutlinedIcon,
            }}
            menuExpanded={expanded}
            onClick={() => !showReleaseNotesIcon && setExpanded(false)}
          />
        </ul>
      </div>
    </nav>
  );
};

export default Sidebar;
